// src/utils/fetchWithAuth.ts
import { auth } from '../firebase.js'
import { apiConfig } from '../config.js'

/**
 * A thin wrapper around fetch that:
 * 1) resolves your base URL from apiConfig
 * 2) grabs the current Firebase ID token
 * 3) sets the Authorization header
 * 4) throws on HTTP errors (with the JSON message, if any)
 * 
 * This version logs everything so you can see exactly 
 * what’s happening (or what’s not being called at all).
 */
const fetchWithAuth = async (endpoint: string, options: RequestInit = {}) => {
  // 1) build full URL
  const base = apiConfig.apiBaseUrl
  const url = `${base}${endpoint}`
  console.log('[fetchWithAuth] 🔗 URL →', url);
  console.log('[fetchWithAuth] options →', options);

  // 2) grab currentUser
  const user = auth.currentUser
  if (!user) {
    console.warn('[fetchWithAuth] ⚠️  No user logged in — aborting', { url })
    throw new Error('Unauthorized: No Firebase user available.')
  }

  // 3) get ID token
  let token: string
  try {
    token = await user.getIdToken()
    console.log('[fetchWithAuth] 🔑 got ID token', token.slice(0, 20) + '…')
  } catch (err) {
    console.error('[fetchWithAuth] ❌ failed to getIdToken', err)
    throw err
  }

  // 4) merge headers
  const headers: Record<string,string> = {
    ...(options.headers as Record<string,string>),
    Authorization: `Bearer ${token}`,
  }
  console.log('[fetchWithAuth] 📤 headers →', headers)

  // 5) actually call fetch
  let response: Response
  try {
    response = await fetch(url, {
      ...options,
      headers,
    })
    console.log(
      '[fetchWithAuth] ⬅️  response status',
      response.status,
      response.statusText
    )
  } catch (err) {
    console.error('[fetchWithAuth] 🚨 network error', err)
    throw err
  }

  // 6) handle no-content
  if (response.status === 204) {
    console.log('[fetchWithAuth] ↩️  no content (204)')
    return
  }

  // 7) handle HTTP error
  if (!response.ok) {
    let msg = `HTTP ${response.status}: ${response.statusText}`
    try {
      const data = await response.json()
      if (data.message) msg = data.message
    } catch (_) {
      console.warn('[fetchWithAuth] ⚠️  could not parse error JSON')
    }
    console.error('[fetchWithAuth] ❌ error →', msg)
    const error = new Error(msg) as any
    error.status = response.status
    throw error
  }

  // 8) parse JSON
  try {
    const payload = await response.json()
    console.log('[fetchWithAuth] ✅ parsed JSON', payload)
    return payload
  } catch (err) {
    console.error('[fetchWithAuth] ❌ failed to parse JSON', err)
    throw err
  }
}

export default fetchWithAuth